/**
 * Benutzerhandbuch — Single Source of Truth für die Hilfe.
 *
 * Jeder Abschnitt: { id, icon, title, keywords[], body (Markdown) }.
 * - id ist der stabile Anker für Direktsprünge (kontextuelle ?-Buttons).
 * - keywords speisen die Hilfe-Suche (zusätzlich zu Titel + Volltext).
 * - body wird mit der MD-Komponente gerendert (sanitisiert).
 *
 * Pflege: bei neuen Features hier den passenden Abschnitt erweitern —
 * das Handbuch wird mit dem Frontend gebaut und ist damit immer
 * versionsgleich mit der UI, auch offline (PWA).
 */

export const HANDBUCH = [
  {
    id: 'ueberblick', icon: '🧭', title: 'Überblick: Was dieses Portal ist',
    keywords: ['konzept', 'pipeline', 'layer', 'schichten', 'einführung', 'start', 'prinzip', 'kuka2me'],
    body: `
Das Portal ist deine **Recherche- und Content-Fabrik** für KUKA2ME: Es recherchiert
automatisch, verdichtet Funde zu Artikel-Ideen, schreibt Entwürfe, prüft Fakten und
bereitet die Verteilung vor — du triffst die Entscheidungen.

**Die Pipeline in fünf Schritten:**

1. **Recherche** — Bots durchsuchen regelmäßig das Web zu deinen Themen und schreiben verifizierte Reports.
2. **Ideen** — eine wöchentliche Synthese (und deine ⊕ Kombinationen) macht daraus Artikel-Ideen.
3. **Aufbereitung** — pro Idee: Gliederung 📝 → Entwurf 📄 → Faktencheck 🔬.
4. **Verteilung** — Ghost-Blog-Draft 👻, LinkedIn 💼, Newsletter ✉, Teaser ⚡, Video 🎬.
5. **Wissen** — alle Reports sind durchsuchbar und befragbar (mit Belegen).

**Drei Grundprinzipien, die überall gelten:**

- **Nichts wird automatisch veröffentlicht.** Ghost bekommt nur Drafts, Videos nur Links — freigeben tust du.
- **Ein schwerer KI-Lauf zur Zeit.** Der Mac Mini arbeitet Gliederungen, Entwürfe & Co. nacheinander ab. Meldungen wie „läuft bereits — bitte warten" sind Schutz, kein Fehler.
- **Alles läuft lokal** (Modelle, Suche, Wissens-Index) — einzige Ausnahme: revid.ai rendert Videos in der Cloud.
`,
  },
  {
    id: 'anmeldung', icon: '🔑', title: 'Anmeldung & Sitzung',
    keywords: ['token', 'login', 'passwort', 'abmelden', 'sitzung abgelaufen', 'bearer'],
    body: `
Der Zugang ist privat und läuft über ein **Bearer-Token** (liegt in Bitwarden).

- **Anmelden:** Token auf dem Anmeldebildschirm eingeben — es wird im Browser gespeichert, du bleibst angemeldet.
- **„Sitzung abgelaufen":** erscheint, wenn das Token rotiert wurde (alle ~90 Tage). Neues Token aus Bitwarden eingeben.
- **Abmelden:** unten in der Seitenleiste (⎋) — löscht das Token aus diesem Browser.
`,
  },
  {
    id: 'chat', icon: '◈', title: 'Chat: mit der KI arbeiten',
    keywords: ['chat', 'modell', 'stil', 'antwort', 'kopieren', 'regenerieren', 'export', 'upload', 'datei', 'pdf', 'suche in chats'],
    body: `
Der Chat-Tab ist dein direkter Draht zu den lokalen Modellen.

- **Neuer Chat / Verlauf:** links in der Seitenleiste. Titel per Doppelklick oder ✎ umbenennen, ⚲ pinnen, 🗑 löschen.
- **Chats durchsuchen:** das Suchfeld in der Seitenleiste findet Wörter in allen Chat-Verläufen — Treffer anklicken öffnet den Chat.
- **Einstellungen (⚙):** Modell, Antwort-Stil und Kreativität (Temperatur). Verfügbar sind **Gemma 4 26B** (gründlich, ~11 tok/s), **Qwen 3.5 27B** (Standard, ~11 tok/s), **Qwen 3.8 27B** (neuer, multimodal, ~6,5 tok/s — denkt kürzer, ist dadurch oft trotzdem schneller fertig) und **Qwen 3.8 27B Uncensored (Test)**. Alle können Bilder lesen und Werkzeuge aufrufen.
- **Antwort-Stil-Pillen** über dem Eingabefeld: schneller Wechsel zwischen Stilen.
- **Dateien:** 📎 hängt Dokumente (z.B. PDF) an — der Inhalt wird dem Modell mitgegeben.
- **Nachricht-Aktionen:** ⎘ kopieren, ↺ letzte Antwort neu generieren, ↓ ganzen Chat als Markdown exportieren, ⌫ Verlauf löschen.
- **Reports & Ideen besprechen:** aus Recherche/Ideen heraus gibt es „◈ Besprechen" — öffnet einen Chat mit dem Inhalt als Kontext.
`,
  },
  {
    id: 'slash', icon: '⚡', title: 'Slash-Befehle im Chat',
    keywords: ['slash', 'befehle', '/web', '/memory', '/ops', '/schedule', 'websuche', 'server status', 'logs'],
    body: `
Tippe **/** im Eingabefeld — ein Menü mit vier Befehlen erscheint (Pfeiltasten + Enter oder klicken):

| Befehl | Zweck |
|---|---|
| **/web** \`Suchbegriff\` | Echte Websuche (SearXNG/DDG) — Ergebnisse fließen in die Antwort |
| **/memory** \`Stichwort\` | Durchsucht deine persönliche Memory-Datei |
| **/ops** \`[logs <n> \\| service <n> \\| system]\` | Server-Status, Service-Check, Logs, System-Info — 4-in-1 |
| **/schedule** \`Thema\` | Legt aus dem Chat heraus einen Recherche-Job an (öffnet das Formular vorbefüllt) |
`,
  },
  {
    id: 'jobs', icon: '⊕', title: 'Recherche-Jobs: anlegen & verwalten',
    keywords: ['job', 'recherche', 'zeitplan', 'täglich', 'wöchentlich', 'einmalig', 'pausieren', 'fehler', 'auto-disable', 'ad-hoc', 'jetzt ausführen'],
    body: `
Ein **Recherche-Job** ist ein Bot, der regelmäßig zu einem Thema recherchiert und Reports schreibt.

**Anlegen:** „+ Neuer Job" → Thema, Modell, Zeitplan (täglich / wöchentlich mit Mehrfach-Wochentagen / 14-tägig / manuell / einmalig-jetzt) + Uhrzeit. Für Feintuning siehe *Erweiterte Job-Optionen*.

**Job-Karte — die Aktionen rechts:**
- ▶ **Jetzt ausführen** — sofortiger Lauf (zusätzlich zum Zeitplan)
- ✎ **Bearbeiten** — alle Einstellungen ändern
- ⏸/▷ **Pausieren/Aktivieren**
- ✕ **Löschen** (mit Rückfrage)

Klick auf die Karte selbst öffnet rechts die **Reports** des Jobs.

**Fehler-Verhalten:** Nach Fehlern zählt ein Zähler hoch (Chip „N Fehler in Folge"); nach **5 Fehlern in Folge** pausiert der Job automatisch und du bekommst eine Push-Nachricht. Fehlertext steht am ⛔-Banner.

**Ad-hoc-Recherchen:** Einmalige Lücken-Recherchen (aus dem Ideen-Tab) sind standardmäßig ausgeblendet — der Schalter „▸ N Ad-hoc-Recherchen anzeigen" blendet sie ein.
`,
  },
  {
    id: 'jobs-erweitert', icon: '🛠', title: 'Erweiterte Job-Optionen',
    keywords: ['system-prompt', 'override', 'triage', 'kern kontext lärm', 'query-rotation', 'engines', 'searxng', 'ddg', 'whitelist', 'blacklist', 'zeitraum', 'iterationen'],
    body: `
Im Job-Formular unter **„▸ Erweitert"**:

- **System-Prompt-Override** — eigene Triage-Logik für den Bot (z.B. KERN/KONTEXT/LÄRM-Regeln). Platzhalter: \`{topic}\`, \`{today}\`. Die Werkzeug-Anweisungen bleiben automatisch erhalten.
- **Query-Rotation** — mehrere Suchanfragen (eine pro Zeile); jeder Lauf nimmt die nächste. Gut gegen immergleiche Treffer.
- **Such-Engines** — SearXNG und/oder DDG; leer = beide mit Fallback.
- **Whitelist/Blacklist-Domains** — Quellen bevorzugen bzw. ausschließen (eine Domain pro Zeile).
- **Zeitraum (Tage)** — nur Treffer aus den letzten N Tagen (1–365).
- **Max Iterationen** — wie viele Suchrunden der Bot maximal dreht (1–15, Standard 5).
`,
  },
  {
    id: 'reports', icon: '📑', title: 'Reports lesen & bewerten',
    keywords: ['report', 'kern', 'quellen', 'score', 'qualität', 'markdown', 'download', 'besprechen', 'verifiziert'],
    body: `
Reports sind Rechercheergebnisse lokaler Modelle — eine Arbeitsgrundlage, keine geprüfte Faktenbasis (siehe Quellen-Hinweis unten).

- **Öffnen:** Job-Karte anklicken → rechts die Report-Liste → Eintrag öffnen.
- **KERN-Chip:** \`N KERN\` zeigt, wie viele **Primärquellen** (DOI, arXiv, EUR-Lex, Institutionen-PDFs …) der Report enthält — der Tooltip schlüsselt KERN/KONTEXT/URLs auf. Faustregel: ab 2 KERN (hervorgehoben) ist ein Report artikel-tragfähig.
- **Quellen sind erreichbarkeits-geprüft — nicht inhaltlich geprüft:** Das System ruft jede URL im Quellen-Block ab und markiert tote Links als „nicht erreichbar". Es prüft **nicht**, ob das Dokument die Aussage im Text auch trägt. Ein Modell kann eine Fundstelle erfinden und mit einem funktionierenden Link garnieren, der auf etwas ganz anderes zeigt — genau das ist am 21.08.2026 passiert (ein Report gab eine Verordnung über Futtermittelzusätze als Änderung der EU-KI-Verordnung aus, Link HTTP 200). **Ein Link mit Status 200 ist kein Beleg.** Rechtsakt-Nummern, Paragrafen, Daten und Zahlen vor der Weiterverwendung gegen die Primärquelle gegenlesen.
- **↓ Markdown** lädt den Report herunter; **💬 Mit KI besprechen** öffnet ihn im Chat.
`,
  },
  {
    id: 'cleaning', icon: '🧹', title: 'Aufräumen: Archiv statt Löschen',
    keywords: ['aufräumen', 'archiv', 'archivieren', 'löschen', 'duplikat', 'redundant', 'wiederherstellen', 'cleaning', 'speicher'],
    body: `
Mit der Zeit sammeln sich schwache Reports an (quellenlos, winzig, doppelt) — sie
verwässern die Treffer in Suche und „Frag deine Recherche". **🧹 Aufräumen**
(Recherche-Tab) hält den Korpus scharf, **ohne Wissen zu vernichten**:

- Der **Compare-Prozess** schlägt Kandidaten vor: Reports **ohne einzige URL**,
  **Mini-Reports** (< 1,5 KB) und **Beinahe-Duplikate** (per Ähnlichkeitsvergleich
  zum neueren Report desselben Themas).
- **Geschützt** und nie vorgeschlagen: Reports, die Quellen einer Idee sind, und
  der jeweils neueste Report jedes Themas.
- Du wählst ab/an und klickst **„N archivieren"** — archivierte Reports
  verschwinden aus Listen, Volltextsuche, Gedächtnis und Drift, **bleiben aber
  gespeichert**: über **📦 Archiv ansehen** jederzeit ↩ wiederherstellbar.
- Es wird **nie automatisch gelöscht oder archiviert** — jede Archivierung ist
  dein Klick (dasselbe Prinzip wie „kein Auto-Publish").

Zur Einordnung: Speicherplatz ist *nicht* der Grund (die Recherche wächst nur
~25 MB pro Jahr) — es geht um die **Präzision deines Wissens-Gedächtnisses**.
`,
  },
  {
    id: 'kombinieren', icon: '⊕', title: 'Recherchen kombinieren (Themenkinder)',
    keywords: ['kombinieren', 'kombi', 'themenkinder', 'kreuz-synthese', 'vorschläge', 'verlauf', 'schnittstelle'],
    body: `
**⊕ Kombinieren** verschmilzt zwei bis vier Themen: Die KI sucht in den neuesten Reports
der gewählten Themen nach **Schnittstellen** und erzeugt daraus neue, offene Ideen
(„Themenkinder") im Ideen-Tab — reine Verdichtung des Vorhandenen, keine neue Websuche.

**So geht's:** Recherche-Tab → „⊕ Kombinieren" → 2–4 Themen anklicken (Themen ohne Reports sind ausgegraut) → „Kombinieren (N)". Nach 1–3 Minuten erscheinen die Themenkinder unter **Offen**, markiert mit ⊕.

- **💡 Vorschläge:** Das System schlägt Paare vor, die sich thematisch überschneiden und noch nie kombiniert wurden — ein Klick übernimmt die Auswahl.
- **Verlauf (rechte Spalte):** jeder Lauf mit Ergebnis (✓ N Ideen / ✗ Fehler) und **↻ Erneut**.
- Unerschlossene Kandidaten zeigt auch die 🗺 *Themen-Landkarte* im Wissen-Tab (🏝-Markierung).
`,
  },
  {
    id: 'ideen-board', icon: '💡', title: 'Ideen-Board: Status & Karten',
    keywords: ['ideen', 'kanban', 'board', 'status', 'offen', 'ausgewählt', 'in arbeit', 'entwurf', 'verworfen', 'nächster schritt', 'fortschritt', 'busy'],
    body: `
Das Board zeigt alle Artikel-Ideen als Kanban nach **Status**:

| Spalte | Bedeutung |
|---|---|
| ○ **Offen** | Frisch aus Synthese/Kombination — noch nicht gesichtet |
| ★ **Ausgewählt** | Von dir markiert — bereit für die Gliederung |
| ✎ **In Arbeit** | Gliederung existiert (wird automatisch gesetzt) |
| 📄 **Entwurf** | Entwurf existiert (automatisch) — prüfen & verteilen |
| ✕ **Verworfen** | Aussortiert (reaktivierbar) |

**Die Karte:** Titel + Kurz-Hook + **Fortschritts-Punkte** 📝📄🔬👻🎬 (kräftig = vorhanden, pulsierend = läuft gerade). Der große Button ist immer der **sinnvolle nächste Schritt** (★ Auswählen → 📝 Gliederung → 📄 Entwurf → 🔬 Faktencheck → 👻 Ghost → ♻ Verteilen). „Details" (oder Klick auf den Titel) öffnet das Detail-Fenster.

**⏳ Busy-Chip** oben: zeigt, welcher Lauf gerade rechnet — solange er sichtbar ist, warten neue schwere Aktionen (Schutz des Mac Mini). Klick springt zur betreffenden Idee.

**Umschalter „Board | 📤 Verteilt":** wechselt zum Verteil-Protokoll (siehe *Verteilen*).
`,
  },
  {
    id: 'ideen-detail', icon: '📋', title: 'Ideen-Detail: Pipeline pro Idee',
    keywords: ['gliederung', 'entwurf', 'faktencheck', 'quellen', 'lücken', 'recherche-lücke', 'notizen', 'löschen', 'besprechen', 'detail', 'modal'],
    body: `
Das Detail-Fenster (Klick auf Titel oder „Details") bündelt alles zu einer Idee:

**Pipeline** — beschriftete Buttons, der nächste Schritt ist hervorgehoben:
- **📝 Gliederung** (1–3 min): Redaktions-Gliederung aus Idee + Quell-Reports.
- **📄 Entwurf** (2–4 min): Artikel-Rohfassung entlang der Gliederung, mit Quellenregister.
- **🔬 Faktencheck** (1–3 min): prüft den Entwurf **gegen das Quellmaterial** — belegte Aussagen, unbelegte Aussagen, Übertreibungen, Urteil.
- ✓ am Button heißt: existiert schon (erneuter Klick erzeugt neu).

**Quellen:** \`📑 Report #N\`-Einträge sind klickbar und öffnen den Quell-Report direkt; dazu die geprüften Web-Quellen der Idee.

**Lücken → Recherche (Loop-Closure):** Das „Lücken"-Feld sowie jeder offene Punkt aus dem Faktencheck hat einen **🔍-Knopf** — er startet eine einmalige, gezielte Nachrecherche (erscheint im Recherche-Tab als Ad-hoc-Job). So schließt das System seine eigenen Wissenslücken.

**Inhalte** (Gliederung/Entwurf/Faktencheck/Kanal-Texte) sind zugeklappt — aufklappen bei Bedarf, Texte haben ⧉ Kopieren.

**Notizen** speichern sich beim Verlassen des Feldes. **Status & Löschen** ganz unten.
`,
  },
  {
    id: 'verteilen', icon: '📤', title: 'Verteilen: Ghost, Social, Video',
    keywords: ['ghost', 'draft', 'veröffentlichen', 'linkedin', 'newsletter', 'teaser', 'repurpose', 'video', 'revid', 'credits', 'art. 50', 'kennzeichnung', 'verteilt', 'protokoll'],
    body: `
Aus einem fertigen Entwurf machst du im Detail-Fenster kanalfertige Formate — **nichts davon wird automatisch veröffentlicht**:

- **👻 Nach Ghost:** legt den Artikel als **Draft** im Blog-CMS an (nie „published"). Freigabe machst du in Ghost.
- **💼 LinkedIn / ✉ Newsletter / ⚡ Teaser:** kanalgerecht formulierte Texte (~1 min, lokal) — mit ⧉ Kopieren zum Selbst-Posten.
- **🎬 Video:** rendert **lokal** (kuka2me-Video-Pipeline: kostenlos, deutsche Stimme, Art.-50-Label eingebrannt) — bequemer im **📤 Posting-Tab**. revid.ai bleibt Cloud-Fallback (Credits).

> **Art. 50 KI-VO:** KI-erzeugte Inhalte vor Veröffentlichung sichten und als KI-generiert kennzeichnen — die App erinnert an den entsprechenden Stellen daran.

**📤 Posting-Tab:** Video-Studio (lokal, kostenlos, KI-Label eingebrannt) und das Verteil-Protokoll „welches Thema ging wohin, wann" — mit **Ghost-Live-Status**. Siehe Abschnitt *Posting*.
`,
  },
  {
    id: 'posting', icon: '📤', title: 'Posting: Video-Studio & Verteil-Zentrale',
    keywords: ['posting', 'video', 'rendern', 'lokal', 'stimme', 'untertitel', 'b-roll', 'pexels', 'caption', 'credits', 'ki-label', 'art. 50', 'verteilt', 'protokoll', 'revid', 'aussprache', 'kai', 'umschrift', 'lexikon', 'take', 'hörprobe', 'kürzel', 'anglizismus'],
    body: `
Der **📤 Posting-Tab** ist die Verteil-Zentrale — hier wird aus fertigen Entwürfen
etwas, das du posten kannst. **Veröffentlicht wird nichts automatisch.**

**🎬 Video-Studio:** Jede Idee mit Entwurf bekommt eine Karte. **„Video erstellen"** macht
alles lokal auf dem Mac Mini — kostenlos: gemma4 schreibt das Sprecher-Skript (60–90 s)
plus Caption, dann rendert die kuka2me-Video-Pipeline (deutsche Stimme Supertonic F4,
wortgenaue Untertitel, geprüfte Pexels-Clips mit Gesichtsfilter, 1080×1920). Das
**KI-Label ist dezent oben rechts eingebrannt** und steht in den MP4-Metadaten (Art. 50).
Erstes Video: 2–5 min (Modelle laden), danach 1–2 min. Das fertige Video spielst du direkt
in der Karte ab.

- **▸ Texte** zeigt Skript, **Caption** (mit offener Frage, ohne Kommentar-Bait) und die
  **Footage-Credits** (Pexels — gehören in die Caption) sowie vorhandene Kanal-Texte — alles mit ⧉ Kopieren.
- **revid** (kleiner Knopf, falls Key vorhanden): rendert stattdessen in der Cloud — kostet Credits; nur als Fallback gedacht.
- Der Chip oben zeigt, ob die lokale Engine bereit ist.

**🔊 Aussprache-Kontrolle** (Block in jeder Karte mit Skript): Synthetische Stimmen verhunzen
Kürzel, Eigennamen und Anglizismen — Anlass war ein Video, in dem „KI" wie der Name „Kai" klang.
Der Workflow kontrolliert genau das, in drei Stufen:
1. **Hörprobe (isoliert):** „🔊 Prüfen" lässt die Stimme jedes Risikowort **einzeln** sprechen und
   Whisper zurückhören — im ganzen Satz würde Whisper den Fehler still reparieren, isoliert nicht.
   Je Wort: *gesprochen als* (was die Stimme bekommt), *gehört*, ✓/⚠/✗ und Läufe (die Stimme ist
   nicht deterministisch, darum 2×). Dauert 20–60 s, braucht keinen Render.
2. **Umschrift:** Bei ⚠/✗ trägst du in die Spalte *Umschrift* ein, wie die Stimme das Wort sprechen
   soll — z. B. Kürzel als Buchstabennamen (KI → „Ka Ih", HR → „Ha Err"), Anglizismen lautschriftlich
   (Reward → „Riwoard"). **Der Untertitel bleibt das Original.** „💾 Umschriften speichern", dann
   erneut prüfen oder neu rendern. Erlaubt sind nur Aussprachevarianten desselben Wortes — ein
   anderes Wort („Betriebsrat → Chef") lehnt das System ab.
3. **Im Render automatisch:** Kürzel + Eigennamen werden vorab angehört, die Stimme wird bis zu
   **2× aufgenommen** und die fertige Aufnahme **gegen das Skript zurückgehört**; der sauberere Take
   kommt ins Video. Der Bericht (Hörprobe, Abweichungen, Take) steht danach in der Karte —
   Chip „✓ sauber" oder „⚠ n auffällig".

Grenze, ehrlich: Kürzel mit Bindestrich vor Vokal („KI-Agenten", „KI-Einsatz") bleiben für die
Stimme wacklig — die Prüfung zeigt es, eine Umschrift hilft manchmal, am besten ist eine andere
Formulierung im Skript („Agenten mit KI"). Das letzte Wort hat dein Ohr: Video vor dem Posten anhören.

**📋 Verteil-Protokoll:** welches Thema ging wohin — Ghost mit **Live-Status** (✅ veröffentlicht / 📝 Draft),
Video (lokal/revid), Kanal-Symbole. Titel-Klick öffnet die Idee.

> **Vor dem Posten, immer:** Video sichten · beim Upload den **KI-Schalter der Plattform** setzen
> (das eingebrannte Label ersetzt ihn nicht) · Footage-Credits in die Caption.
`,
  },
  {
    id: 'wissen', icon: '🧠', title: 'Wissen: Fragen & Suchen',
    keywords: ['wissen', 'fragen', 'rag', 'belege', 'zitate', 'suche', 'volltext', 'index', 'nachziehen', 'gedächtnis'],
    body: `
Der Wissen-Tab macht deine Reports zum **befragbaren Gedächtnis** — komplett lokal.

- **💬 Fragen:** Stelle eine Frage („Was wissen wir über …?"). Die Antwort entsteht **ausschließlich aus deinen Reports**, jede Kernaussage trägt einen Beleg wie \`[R187]\` — die Belege unter der Antwort sind klickbar und öffnen den Report. Dauert ~30–60 s (lokales Modell). Gibt der Korpus nichts her, sagt das System das ehrlich.
- **🔍 Suchen:** Volltextsuche über alle Reports mit hervorgehobenen Fundstellen — Treffer öffnen den Report.
- **🧠 Index-Chip** oben: zeigt, wie viele Reports für „Fragen" indexiert sind. Neue Reports indexieren sich automatisch; „↻ nachziehen" holt Fehlende nach.
`,
  },
  {
    id: 'landkarte-drift', icon: '🗺', title: 'Landkarte & Drift-Radar',
    keywords: ['landkarte', 'karte', 'themen', 'verbindungen', 'isoliert', 'drift', 'radar', 'begriffe', 'trend', 'verschiebung'],
    body: `
Zwei Analyse-Modi im Wissen-Tab:

- **🗺 Landkarte:** Welche Themen sind miteinander **verbunden** (über ⊕ Kombinationen und Ideen, die Reports mehrerer Themen verschmelzen) — und welche sind **🏝 isoliert**? Isolierte Themen sind deine unerschlossenen Kombi-Chancen.
- **📈 Drift:** Was verschiebt sich pro Thema? Vergleicht die Begriffe des neuesten Reports mit den vorherigen: **📈 neue** Begriffe und **📉 abgeflaute** (vorher stabil, jetzt weg). Braucht mind. 3 Reports je Thema. Es ist bewusst ein *Radar* (Begriffs-Heuristik) — ein Hinweis, wo genauer hinzuschauen lohnt, kein Beweis.
`,
  },
  {
    id: 'push-meldungen', icon: '🔔', title: 'Push-Nachrichten & Meldungen verstehen',
    keywords: ['push', 'benachrichtigung', 'alarm', 'watchdog', '409', 'läuft bereits', 'fehler', 'leer', 'warten', 'troubleshooting', 'hilfe'],
    body: `
**Push-Benachrichtigungen** (Seitenleiste → Aktivieren; „Test" prüft den Kanal) melden dir:
fertige Recherchen, automatisch pausierte Jobs — und **Watchdog-Alarme**, falls das lokale
KI-System klemmt (es repariert sich dann selbst; der Alarm ist zur Info).

**Häufige Meldungen — und was sie bedeuten:**

| Meldung | Bedeutung |
|---|---|
| „… läuft bereits — bitte warten" | Lastschutz: der Mac Mini arbeitet schwere Läufe **nacheinander** ab. Der ⏳-Chip oben zeigt, was läuft. Kurz warten, dann klappt der Klick. |
| „Aufbereitung/Kombination läuft gerade" | Dasselbe Prinzip für Lücken-Recherche & Kombination. |
| „Video-Render fehlgeschlagen — erneut klicken" | Erneut klicken setzt den **vorhandenen** Render fort (kein Doppel-Credit). |
| „Sitzung abgelaufen" | Token neu eingeben (siehe *Anmeldung*). |
| Antwort/Lauf endet leer oder mit Fehler | Einfach erneut starten — die Läufe haben eingebaute Wiederholungen; hartnäckige Klemmer behebt der Watchdog automatisch (mit Push-Info). |
`,
  },
];

/**
 * Hilfe-Suche: durchsucht Titel (stark), Keywords (mittel) und Volltext (schwach).
 * Liefert Abschnitte mit Trefferscore + Kontext-Snippet, bester zuerst.
 */
export function searchHandbuch(query) {
  const q = (query || '').trim().toLowerCase();
  if (q.length < 2) return [];
  const terms = q.split(/\s+/).filter(t => t.length >= 2);
  const scored = [];
  for (const sec of HANDBUCH) {
    let score = 0;
    const title = sec.title.toLowerCase();
    const body = sec.body.toLowerCase();
    for (const t of terms) {
      if (title.includes(t)) score += 5;
      if (sec.keywords.some(k => k.includes(t))) score += 3;
      if (body.includes(t)) score += 1;
    }
    if (score === 0) continue;
    // Snippet um den ersten Volltext-Treffer herum
    let snippet = '';
    const idx = body.indexOf(terms.find(t => body.includes(t)) || '');
    if (idx >= 0) {
      const start = Math.max(0, idx - 60);
      snippet = (start > 0 ? '…' : '') +
        sec.body.slice(start, idx + 90).replace(/[#*|>`]/g, ' ').replace(/\s+/g, ' ').trim() + '…';
    }
    scored.push({ ...sec, score, snippet });
  }
  scored.sort((a, b) => b.score - a.score);
  return scored;
}
